import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ShowcaseItem = {
  id: string;
  image: string;
  original: string | null;
  style: string;
  room: string;
};

const pickImage = (d: any): string | null =>
  (Array.isArray(d.public_variations) && d.public_variations[0]) ||
  (Array.isArray(d.all_variations) && d.all_variations[0]) ||
  d.transformed_image_url ||
  d.original_image_url ||
  null;

/**
 * Fetches real public transformations from Explore (saved_designs where is_public=true)
 * to power the homepage Before/After showcase, style rail, and community wall.
 * Read-only. Fails silently — consumers fall back to bundled assets.
 */
export function useExploreShowcase(limit = 40) {
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("saved_designs")
          .select(
            "id, transformed_image_url, original_image_url, design_style, room_type, all_variations, public_variations"
          )
          .eq("is_public", true)
          .order("created_at", { ascending: false })
          .limit(limit);

        if (cancel || !data) return;
        const mapped: ShowcaseItem[] = data
          .map((d: any) => ({
            id: d.id,
            image: pickImage(d) as string,
            original: d.original_image_url ?? null,
            style: d.design_style ?? "",
            room: d.room_type ?? "",
          }))
          .filter((x) => !!x.image);
        setItems(mapped);
      } catch {
        /* silent */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [limit]);

  return { items, loading };
}

/** Group items by canonical room type (lowercased). */
export function groupByRoom(items: ShowcaseItem[]) {
  const map = new Map<string, ShowcaseItem[]>();
  for (const it of items) {
    const key = (it.room || "").toLowerCase();
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(it);
  }
  return map;
}
